import { SelectedFilter } from "../../Shared/scriptts/interfaces";
import { isEmpty } from '../../Shared/scriptts/utilities';
import { params, aliasMapping, executeSearchUrl, displayResults } from '../search-main';
import { URLCriteriaHelper } from '../search-urlcriteriahelper';
import { isBrandedSearchFilter, isUserPreferenceFilter, toggleUserPreferences, setFilterCount, removeParam, getPreferencToggle, preferences } from '../search-filters-main';

const filterPills = document.querySelector<HTMLElement>('#filter-pills');

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll<HTMLButtonElement>('#remove-filters, #remove-filters_m').forEach(elem => elem.addEventListener('click', removeAllClicked));
});

/**
 * Display pills for filters that have been applied to the search
 */
export function displayFilterPills() {
    if (!filterPills) {
        return 0;
    }
    filterPills.innerHTML = '';

    let filterCount = buildFilterPills() + buildNonSelectableFilterPills();

    if (params.GradeSearch || !isEmpty(params.RemunerationMinimumAmount)) {
       filterCount += buildPayPills();
    }

    if (filterCount > 0) {
        document.querySelector<HTMLElement>('#filter-pills-container')?.classList.remove('hidden');

        if (document.querySelectorAll<HTMLButtonElement>('[data-filter]').length < 1) {
            document.querySelector<HTMLButtonElement>('#remove-filters')?.classList.add('hidden');
        }
        else {
            document.querySelector<HTMLButtonElement>('#remove-filters')?.classList.remove('hidden');
        }
    }
    else {
        document.querySelector<HTMLElement>('#filter-pills-container')?.classList.add('hidden');
    }

    setFilterCount(filterCount);
}

function buildFilterPills(): number {
    if (!filterPills) {
        return 0;
    }
    let count = 0;
    document.querySelectorAll<HTMLInputElement>('input').forEach(elem => {
        if (elem.checked || (elem.name === 'radius' && !document.querySelector<HTMLElement>('#radius-filter')?.classList.contains('hidden'))) {
            const filter: SelectedFilter = {
                Name: getPillName((elem.name === 'radius' ? `${elem.value} miles` : document.querySelector<HTMLLabelElement>(`[for=${elem.id}]`)?.innerText || ''), elem.id),
                Value: elem.value,
                FilterType: elem.name,
                IsBrandedSearch: isBrandedSearchFilter(elem.name, elem.value),
                IsUserPreference: isUserPreferenceFilter(aliasMapping[elem.name]?.JOAQueryName || '', elem.value)
            }
            filterPills.appendChild(getFilterPill(filter));
            ++count;
        }
    });
    return count;
}

function buildPayPills(): number {
    if (!filterPills || (!params.GradeSearch && isEmpty(params.RemunerationMinimumAmount))) {
        return 0;
    }
    let count = 0;
    if (params.GradeSearch && params.GradeBucket.length > 0) {
        const pillName = `GS ${params.GradeBucket[0]} - GS ${params.GradeBucket[params.GradeBucket.length - 1]}`;
        const filter: SelectedFilter = {
            Name: pillName,
            Value: pillName,
            FilterType: 'GradeBucket',
            IsBrandedSearch: isBrandedSearchFilter('GradeBucket', pillName),
            IsUserPreference: isBrandedSearchFilter('GradeBucket', pillName)
        }
        filterPills.appendChild(getFilterPill(filter));
        ++count;
    }
    else if (!params.GradeSearch && !isEmpty(params.RemunerationMinimumAmount)) {
        const pillName = `${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(parseInt(params.RemunerationMinimumAmount || '0')).toString()} (Minimum salary)`;
        const filter: SelectedFilter = {
            Name: pillName,
            Value: pillName,
            FilterType: 'RemunerationMinimumAmount',
            IsBrandedSearch: isBrandedSearchFilter('RemunerationMinimumAmount', pillName),
            IsUserPreference: isBrandedSearchFilter('RemunerationMinimumAmount', pillName)
        }
        filterPills.appendChild(getFilterPill(filter));
        ++count;
    }
    return count;
}

function buildNonSelectableFilterPills(): number {
    if (!filterPills) {
        return 0;
    }

    const nonSelectableFilterParams = [
        'LocationName',
        'SocTitle',
        'CyberWorkRole',
        'JobTitle',
        'JobGradeCode'
    ];
    let count = 0;

    nonSelectableFilterParams.forEach(param => {
        const paramValue = params[param];

        if (!paramValue) {
            return;
        }
        if (Array.isArray(paramValue)) {
            paramValue.forEach(val => {
                const filter: SelectedFilter = {
                    Name: val,
                    Value: val,
                    FilterType: aliasMapping[param].ElasticName,
                    IsBrandedSearch: isBrandedSearchFilter(aliasMapping[param].ElasticName, val),
                    IsUserPreference: isUserPreferenceFilter(param, val)
                };
                filterPills.appendChild(getFilterPill(filter));
                ++count;
            });
        }
        else {
            const filter: SelectedFilter = {
                Name: paramValue,
                Value: paramValue,
                FilterType: aliasMapping[param].ElasticName,
                IsBrandedSearch: isBrandedSearchFilter(aliasMapping[param].ElasticName, paramValue),
                IsUserPreference: isUserPreferenceFilter(param, paramValue)
            };
        }
    });
    return count;
}

function getFilterPill(filter: SelectedFilter): HTMLDivElement {
    const bgColor = filter.IsBrandedSearch ? 'bg-primary-darker' : filter.IsUserPreference ? 'bg-green-cool-30' : 'bg-gray-dark';
    const textColor = filter.IsUserPreference ? '' : 'text-white';
    const fillColor = filter.IsUserPreference ? '' : 'fill-white';
    const container = document.createElement('div');

    container.className = `font-semibold rounded flex flex-nowrap gap-2 justify-between text-sm px-2 py-1 mb-2 ${bgColor} ${textColor}`;
    container.innerHTML = `<span class="whitespace-nowrap">${filter.Name}</span>`;

    if (!filter.IsBrandedSearch) {
        const closeButton = document.createElement('button');

        closeButton.className = 'cursor-pointer';
        closeButton.id = `close-${Math.round(Math.random() * 10000)}`;
        closeButton.setAttribute('data-filter', filter.FilterType);
        closeButton.setAttribute('data-filter-name', filter.Name);
        closeButton.setAttribute('data-filter-value', filter.Value);
        closeButton.setAttribute('title', `Remove ${filter.Name} filter`);
        closeButton.innerHTML = `<svg class="w-4 h-4 pointer-events-none ${fillColor}" role="presentation"><use xlink:href="/img/icons/sprite.svg#close"></use></svg>`;
        closeButton.addEventListener('click', closeButtonClicked);
        container.appendChild(closeButton);
    }

    return container;
}

function getPillName(name: string, id: string) {
    let pillName = name;
    if (pillName.indexOf('miles') > -1) {
        return pillName;
    }
    pillName = pillName.substring(0, pillName.indexOf('(')).trim();

    switch (id) {
        case "hiring_path-graduates":
            return pillName += " (Hiring path)";
        case "work_type-15326":
            return pillName += " (Appointment type)";
        case "work_type-15522":
            return pillName += " (Appointment type)";
        case "work_schedule-4":
            return pillName += " (Work schedule)";
        case "work_schedule-6":
            return pillName += " (Work schedule)";
        case "work_type-15327":
            return pillName += " (Appointment type)";
        case "security_clearance-0":
            return pillName += " (Security clearance)";
        case "travel_percentage-0":
            return pillName += " (Travel percentage)";
        default:
            return pillName;
    }
}

function closeButtonClicked(evt: Event) {
    const elem = <HTMLButtonElement>(evt.target);

    removeParam(aliasMapping[elem.getAttribute('data-filter') || '']?.JOAQueryName || '', elem.getAttribute('data-filter-value') || '');

    if (getPreferencToggle()) {
        let userPrefsFound = false;
        const closeButtons = document.querySelectorAll<HTMLButtonElement>('[data-filter]');
        
        for (const i in closeButtons) {
            const button = closeButtons[i];            
            if (!button || isNaN(parseInt(i)) || (elem.id == button.id)) {
                continue;
            }

            const filterType = aliasMapping[button?.getAttribute('data-filter') || '']?.JOAQueryName || '';
            const filterValue = button?.getAttribute('data-filter-value') || '';

            if (isUserPreferenceFilter(filterType, filterValue)) {
                userPrefsFound = true;
                break;
            }
        }
        if (!userPrefsFound) {
            toggleUserPreferences(false);
        }
    }

    URLCriteriaHelper.reconstructUrl();
    displayResults(executeSearchUrl, false, true);
}

function removeAllClicked() {
    document.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach(elem => {
        const param = aliasMapping[elem.getAttribute('data-filter') || '']?.JOAQueryName || '';
        const value = elem.getAttribute('data-filter-value') || '';

        if (!isBrandedSearchFilter(param, value)) {
            removeParam(param, value);
        }
    });
    
    URLCriteriaHelper.reconstructUrl();
    displayResults(executeSearchUrl, false, true).then(() => toggleUserPreferences(false));
}
